import styled, { css } from 'styled-components';
import {
  ProductImagesValuesAttribute,
  ProductTextValuesAttribute,
} from 'src/models/pim/attributes';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import { Paragraph } from 'src/components/common/typography/Text';
import { PimImage } from 'src/components/common/PimImage';
import { media } from 'src/styles/designs';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { casePathEmptyString } from 'src/utils/casePathEmptyString';
import { dirRTL } from 'src/styles/theme';

export interface ProductPageHowItWorksItemProps {
  title: ProductTextValuesAttribute['item'] | undefined;
  description: ProductTextValuesAttribute['item'] | undefined;
  image: ProductImagesValuesAttribute['item']['value'][number] | undefined;
  singular?: boolean;
}

const Name = styled.h4`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text.palette.primary};
  margin-top: 16px;
  & + p {
    margin-top: 12px;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 335px;

  ${media.w.greaterThanOrEqual.desktop} {
    width: 426px;
    height: 516px;
    margin-bottom: 16px;
  }
`;

const ImageInnerContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const ContentContainer = styled.div`
  max-width: 480px;
  margin: 0 auto 0 0;
  ${dirRTL} {
    margin: 0 0 0 auto;
  }
`;

const Container = styled.div<{ $singular?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${media.w.greaterThanOrEqual.desktop} {
    ${casePathEmptyString('$singular')(css`
      flex-direction: row-reverse;
      max-width: 1120px;
      ${ImageContainer} {
        width: 516px;
        height: 595px;
        margin-top: -78px;
      }
      ${ImageInnerContainer} {
        height: 725px;
      }
    `)}
  }
`;

export const ProductPageHowItWorksItem = ({
  title,
  description,
  image,
  singular,
}: ProductPageHowItWorksItemProps) => {
  const translateFn = useTranslateItem();

  return (
    <Container $singular={singular}>
      {image && (
        <ImageContainer>
          <ImageInnerContainer>
            <PimImage
              src={image.directus_files_id.id}
              layout="fill"
              objectFit="cover"
              sizes="100vw"
            />
          </ImageInnerContainer>
        </ImageContainer>
      )}

      <ContentContainer>
        {title && (
          <Name>
            <Markdown>{translateFn(title).value}</Markdown>
          </Name>
        )}

        {description && (
          <Paragraph>
            <Markdown>{translateFn(description).value}</Markdown>
          </Paragraph>
        )}
      </ContentContainer>
    </Container>
  );
};
